import { Link } from 'react-router-dom';
import { FolderLock, CreditCard, User, Cloud, Chrome, Globe, Smartphone } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import NavigationHeader from '../components/NavigationHeader';
import { getEnvironmentConfig } from '@safermark/config';
import { getRecurringPlan, formatPrice } from '@safermark/shared';

export default function DashboardPage() {
  const { isPremium } = useAuth();
  const config = getEnvironmentConfig();
  const monthlyPlan = getRecurringPlan('monthly');

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Dashboard</h2>
          <p className="text-gray-600">
            Welcome to your secure vault dashboard. Manage your encrypted bookmarks and notes.
          </p>
        </div>

        {/* Status Card */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Account Status</h3>
                <p className="text-gray-600 mt-1">
                  You are currently on the{' '}
                  <span className={`font-semibold ${isPremium ? 'text-blue-600' : 'text-gray-900'}`}>
                    {isPremium ? 'Premium' : 'Free'}
                  </span>{' '}
                  plan
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {isPremium ? (
                  <div className="flex items-center text-blue-600">
                    <Cloud className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">Cloud Sync Active</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-600">
                    <Chrome className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">Extension Only</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Link
            to="/vaults"
            className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="flex items-center mb-4">
              <FolderLock className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Vaults</h3>
            <p className="text-gray-600 text-sm">
              Create, unlock, and manage your encrypted vaults
            </p>
          </Link>

          <Link
            to="/subscription"
            className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="flex items-center mb-4">
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Subscription</h3>
            <p className="text-gray-600 text-sm">
              Manage your subscription and billing settings
            </p>
          </Link>

          <Link
            to="/profile"
            className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="flex items-center mb-4">
              <User className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Profile</h3>
            <p className="text-gray-600 text-sm">
              Update your account information and settings
            </p>
          </Link>

          <a
            href={config.extensionDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="flex items-center mb-4">
              <Chrome className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Extension</h3>
            <p className="text-gray-600 text-sm">
              Download the Chrome extension for local access
            </p>
          </a>
        </div>

        {/* Upgrade Banner for Free Users */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">Upgrade to Premium</h3>
                <p className="text-blue-100 mb-4">
                  Unlock cloud sync, web access, and cross-device functionality for just {formatPrice(monthlyPlan?.price || 2)}/month
                </p>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center">
                    <Cloud className="h-4 w-4 mr-2" />
                    <span>Cloud Sync</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="h-4 w-4 mr-2" />
                    <span>Web Access</span>
                  </div>
                  <div className="flex items-center">
                    <Smartphone className="h-4 w-4 mr-2" />
                    <span>Mobile Optimized</span>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/subscription"
                  className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  Upgrade Now
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity / Instructions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Getting Started</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Install the Chrome Extension</h4>
                <p className="text-gray-600 text-sm">
                  Start with our free Chrome extension for local-only vault management
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Create Your First Vault</h4>
                <p className="text-gray-600 text-sm">
                  Set up an encrypted vault to store your bookmarks and notes securely
                </p>
              </div>
            </div>
            
            {!isPremium && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  3
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Upgrade for Cloud Features</h4>
                  <p className="text-gray-600 text-sm">
                    Upgrade to Premium to sync your vaults across devices and access them from any browser
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}